import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  isTenantSlugValid,
  sanitizeTenantSlugInput,
} from "@/components/pages/tenants/utils/slug";
import { useAdminRegions } from "@/hooks/useAdminRegions";
import { useRegisterTenantWithAdmin } from "../hooks/useTenants";

interface TenantCreateDialogProps {
  open: boolean;
  onClose: () => void;
}

export const TenantCreateDialog: React.FC<TenantCreateDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { mutateAsync, isPending } = useRegisterTenantWithAdmin();
  const { data: regions, isLoading: isRegionsLoading } = useAdminRegions({
    activeOnly: true,
  });

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState("Pending");
  const [defaultRegion, setDefaultRegion] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const slugValue = slug.trim();
  const isSlugInvalid = slugValue !== "" && !isTenantSlugValid(slugValue);

  useEffect(() => {
    if (!defaultRegion && regions && regions.length > 0) {
      setDefaultRegion(regions[0].code);
    }
  }, [regions, defaultRegion]);

  const reset = () => {
    setName("");
    setSlug("");
    setStatus("Pending");
    setDefaultRegion(regions && regions.length > 0 ? regions[0].code : "");
    setAdminEmail("");
    setAdminPassword("");
    setAdminFirstName("");
    setAdminLastName("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isTenantSlugValid(slugValue)) {
      toast.error(t("tenants.validation.slug"));
      return;
    }

    try {
      await mutateAsync({
        tenant: {
          name: name.trim(),
          slug: slugValue,
          status,
          defaultRegion: defaultRegion.trim(),
          features: {},
        },
        admin: {
          username: adminEmail.trim(),
          email: adminEmail.trim(),
          password: adminPassword,
          ...(adminFirstName.trim() !== ""
            ? { firstName: adminFirstName.trim() }
            : {}),
          ...(adminLastName.trim() !== ""
            ? { lastName: adminLastName.trim() }
            : {}),
        },
      });
      toast.success(t("tenants.create.successToast"));
      handleClose();
    } catch {
      toast.error(t("tenants.create.errorToast"));
    }
  };

  const getStatusLabel = (statusValue: string) => {
    switch (statusValue) {
      case "Pending":
      case "pending":
        return t("tenants.statuses.pending");
      case "Active":
        return t("tenants.statuses.active");
      case "Suspended":
        return t("tenants.statuses.suspended");
      default:
        return statusValue;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("tenants.create.title")}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <div className="-mx-6 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6">
            <div className="text-foreground text-sm font-semibold">
              {t("tenants.create.tenantSection")}
            </div>
            <Input
              label={t("tenants.create.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              label={t("tenants.create.slug")}
              value={slug}
              onChange={(e) => setSlug(sanitizeTenantSlugInput(e.target.value))}
              description={
                isSlugInvalid
                  ? t("tenants.validation.slug")
                  : t("tenants.validation.slugHint")
              }
              aria-invalid={isSlugInvalid}
              required
            />
            <div className="flex flex-col gap-1.5">
              <span className="text-sm leading-none font-medium text-zinc-900">
                {t("tenants.create.defaultRegion")}
              </span>
              <Select
                value={defaultRegion}
                onValueChange={setDefaultRegion}
                disabled={isRegionsLoading || !regions || regions.length === 0}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      isRegionsLoading
                        ? t("common.loading")
                        : t("tenants.create.defaultRegionPlaceholder")
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {regions?.map((region) => (
                    <SelectItem key={region.code} value={region.code}>
                      {region.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm leading-none font-medium text-zinc-900">
                {t("tenants.create.status")}
              </span>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">
                    {getStatusLabel("Pending")}
                  </SelectItem>
                  <SelectItem value="Active">
                    {getStatusLabel("Active")}
                  </SelectItem>
                  <SelectItem value="Suspended">
                    {getStatusLabel("Suspended")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="text-foreground pt-2 text-sm font-semibold">
              {t("tenants.create.adminSection")}
            </div>
            <Input
              label={t("tenants.create.adminEmail")}
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <Input
              label={t("tenants.create.adminPassword")}
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t("tenants.create.adminFirstName")}
                value={adminFirstName}
                onChange={(e) => setAdminFirstName(e.target.value)}
                autoComplete="given-name"
              />
              <Input
                label={t("tenants.create.adminLastName")}
                value={adminLastName}
                onChange={(e) => setAdminLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={handleClose}
            >
              {t("tenants.create.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                isPending || !isTenantSlugValid(slugValue) || !defaultRegion
              }
            >
              {t("tenants.create.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
